const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    --font: var(--ctl-sf-font, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    --bg: var(--ctl-sf-bg, var(--ctl-color-bg));
    --surface: var(--ctl-sf-surface, var(--ctl-color-surface));
    --text: var(--ctl-sf-text, var(--ctl-color-text));
    --muted: var(--ctl-sf-muted, var(--ctl-color-muted));
    --border: var(--ctl-sf-border, var(--ctl-color-border));
    --brand: var(--ctl-sf-brand, var(--ctl-color-brand));

    --container: var(--ctl-sf-container, 1120px);
    --gutter: var(--ctl-sf-gutter, var(--ctl-space-4));

    --header-bg: var(--ctl-sf-header-bg, var(--surface));
    --header-border: var(--ctl-sf-header-border, var(--border));
    --header-height: var(--ctl-sf-header-height, 64px);

    --footer-bg: var(--ctl-sf-footer-bg, #f8fafc);
    --footer-border: var(--ctl-sf-footer-border, var(--border));

    --radius: var(--ctl-sf-radius, var(--ctl-radius-14));
    --shadow: var(--ctl-sf-shadow, 0 var(--ctl-space-1-5) var(--ctl-space-4-5) var(--ctl-color-overlay-5));
    --focus: var(--ctl-sf-focus-ring, 0 0 0 3px var(--ctl-color-overlay-5));

    --drawer-width: var(--ctl-sf-drawer-width, 320px);

    display:block;
    box-sizing: border-box;
    font-family: var(--font);
    color: var(--text);
    background: var(--bg);
  }

  /* Container */
  .container{
    max-width: var(--container);
    margin: 0 auto;
    padding: 0 var(--gutter);
  }

  /* Promo strip */
  .promo{
    background: var(--header-bg);
    background: linear-gradient(135deg, 
      color-mix(in srgb, var(--brand) 12%, var(--header-bg)) 0%,
      color-mix(in srgb, var(--brand) 8%, var(--header-bg)) 100%
    );
    border-bottom: 1px solid var(--header-border);
    border-bottom: 1px solid color-mix(in srgb, var(--brand) 22%, var(--header-border));
    color: var(--text);
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-normal);
  }
  .promoInner{
    display:flex;
    align-items:center;
    justify-content: center;
    gap: var(--ctl-space-2-5);
    min-height: 42px;
    padding: var(--ctl-space-2) 0;
  }
  .promo:empty{ display:none; }
  .promo ::slotted(*){ 
    max-width: 100%;
  }

  /* Header */
  header{
    background: var(--header-bg);
    border-bottom: 1px solid var(--header-border);
    box-shadow: 0 1px 3px var(--ctl-color-overlay-2);
  }
  :host([sticky]) header{
    position: sticky;
    top: 0;
    z-index: 20;
    box-shadow: 0 var(--ctl-space-0-5) var(--ctl-space-2) var(--ctl-color-overlay-4);
  }

  .headerInner{
    height: var(--header-height);
    display:flex;
    align-items:center;
    justify-content: space-between;
    gap: var(--ctl-space-3-5);
  }

  .brand{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2-5);
    min-width: 0;
    font-size: var(--ctl-font-size-xl);
    font-weight: var(--ctl-font-weight-black);
    letter-spacing: -0.01em;
  }

  .brand ::slotted(*){ 
    max-width: 100%; 
  }

  nav{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-4);
    min-width: 0;
  }
  nav ::slotted(*){ 
    white-space: nowrap;
    font-weight: var(--ctl-font-weight-normal);
    transition: opacity 0.15s ease;
  }
  nav ::slotted(*:hover){
    opacity: 0.7;
  }

  .actions{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-3);
    justify-content:flex-end;
    flex-wrap: wrap;
  }

  /* Mobile button */
  .menuBtn{
    all: unset;
    cursor: pointer;
    width: 42px;
    height: 42px;
    display:none;
    align-items:center;
    justify-content:center;
    border-radius: var(--ctl-radius-10);
    transition: background 0.15s ease;
  }
  .menuBtn:hover{
    background: color-mix(in srgb, var(--border) 50%, transparent);
  }
  .menuBtn:focus-visible{
    outline: none;
    box-shadow: var(--focus);
  }
  .menuBtn:active{
    transform: scale(0.95);
  }
  .hamburger{
    width: 18px;
    height: 12px;
    position: relative;
  }
  .hamburger span{
    position:absolute;
    left:0; right:0;
    height: var(--ctl-space-0-5);
    border-radius: var(--ctl-radius-full);
    background: var(--text);
    opacity: .9;
  }
  .hamburger span:nth-child(1){ top:0; }
  .hamburger span:nth-child(2){ top:5px; }
  .hamburger span:nth-child(3){ top:10px; }

  /* Main */
  main{
    padding: 0 0 var(--ctl-space-6);
    min-height: 50vh;
  }

  /* Footer */
  footer{
    background: var(--footer-bg);
    border-top: 1px solid var(--footer-border);
    padding: var(--ctl-space-12) 0 var(--ctl-space-5);
    margin-top: var(--ctl-space-12);
  }
  .footerGrid{
    display:grid;
    grid-template-columns: 1.2fr 1fr 1fr 1fr;
    gap: var(--ctl-space-6);
    align-items:start;
  }
  .footerGrid ::slotted(*){
    line-height: 1.6;
  }
  .footerBottom{
    margin-top: 32px;
    padding-top: var(--ctl-space-5);
    border-top: 1px solid color-mix(in srgb, var(--footer-border) 70%, transparent);
    display:flex;
    align-items:center;
    justify-content: space-between;
    gap: var(--ctl-space-4);
    flex-wrap: wrap;
    color: var(--muted);
    font-size: var(--ctl-font-size-md);
  }

  /* Drawer overlay */
  .overlay{
    position: fixed;
    inset: 0;
    background: var(--ctl-color-overlay-7);
    backdrop-filter: blur(1px);
    display:none;
    z-index: 50;
  }
  :host([open]) .overlay{ display:block; }

  .drawer{
    position: fixed;
    top: 0;
    right: 0;
    height: 100%;
    width: min(var(--drawer-width), 92vw);
    background: var(--surface);
    border-left: 1px solid var(--border);
    box-shadow: -8px 0 32px var(--ctl-color-overlay-6);
    transform: translateX(100%);
    transition: transform .22s cubic-bezier(0.4, 0.0, 0.2, 1);
    display:flex;
    flex-direction: column;
    z-index: 60;
  }
  :host([open]) .drawer{
    transform: translateX(0);
  }

  .drawerHeader{
    height: var(--header-height);
    display:flex;
    align-items:center;
    justify-content: space-between;
    gap: var(--ctl-space-3);
    padding: 0 20px;
    border-bottom: 1px solid var(--border);
    background: var(--header-bg);
  }
  .drawerTitle{
    font-weight: var(--ctl-font-weight-black);
    font-size: var(--ctl-font-size-base);
    letter-spacing: -.01em;
    overflow:hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .closeBtn{
    all: unset;
    cursor: pointer;
    width: 40px;
    height: 40px;
    border-radius: var(--ctl-radius-10);
    display:flex;
    align-items:center;
    justify-content:center;
    transition: background 0.15s ease;
  }
  .closeBtn:hover{
    background: color-mix(in srgb, var(--border) 50%, transparent);
  }
  .closeBtn:focus-visible{
    outline: none;
    box-shadow: var(--focus);
  }
  .closeBtn:active{
    transform: scale(0.95);
  }
  .x{
    width: 18px; height: 18px;
    position: relative;
  }
  .x::before, .x::after{
    content:"";
    position:absolute;
    left: var(--ctl-space-2);
    top: var(--ctl-space-0-5);
    width: var(--ctl-space-0-5);
    height: var(--ctl-space-3-5);
    background: var(--text);
    border-radius: var(--ctl-radius-full);
  }
  .x::before{ transform: rotate(45deg); }
  .x::after{ transform: rotate(-45deg); }

  .drawerBody{
    padding: var(--ctl-space-5) 20px;
    display:flex;
    flex-direction: column;
    gap: var(--ctl-space-3);
    overflow:auto;
  }

  /* Slots */
  .promoSlot:empty{ display:none; }
  .footerSlot:empty{ display:none; }

  /* Responsive */
  @media (max-width: 860px){
    nav{ display:none; }
    .actions{ display:none; }
    .menuBtn{ display:flex; }
    main{ padding-top: var(--ctl-space-3-5); }
    .footerGrid{ grid-template-columns: 1fr 1fr; }
  }

  @media (max-width: 520px){
    .footerGrid{ grid-template-columns: 1fr; }
  }
</style>

<!-- Promo strip -->
<div class="promo" part="promo">
  <div class="container promoInner">
    <div class="promoSlot"><slot name="promo"></slot></div>
    <div class="promoRight"><slot name="promo-right"></slot></div>
  </div>
</div>

<header part="header">
  <div class="container headerInner">
    <div class="brand" part="brand">
      <slot name="brand"></slot>
    </div>

    <nav part="nav" aria-label="Primary">
      <slot name="nav"></slot>
    </nav>

    <div class="actions" part="actions">
      <slot name="actions"></slot>
    </div>

    <button class="menuBtn" part="menuButton" type="button" aria-label="Open menu">
      <div class="hamburger" aria-hidden="true">
        <span></span><span></span><span></span>
      </div>
    </button>
  </div>
</header>

<main part="main">
  <div class="container">
    <slot></slot>
  </div>
</main>

<footer part="footer">
  <div class="container">
    <div class="footerSlot">
      <slot name="footer"></slot>
    </div>

    <!-- Optional default footer layout if user doesn't provide slot="footer" -->
    <div class="footerDefault" part="footerDefault">
      <div class="footerGrid" part="footerGrid">
        <div>
          <slot name="footer-brand"></slot>
        </div>
        <div><slot name="footer-col-1"></slot></div>
        <div><slot name="footer-col-2"></slot></div>
        <div><slot name="footer-col-3"></slot></div>
      </div>

      <div class="footerBottom" part="footerBottom">
        <div><slot name="footer-bottom-left"></slot></div>
        <div><slot name="footer-bottom-right"></slot></div>
      </div>
    </div>
  </div>
</footer>

<!-- Drawer -->
<div class="overlay" part="overlay"></div>
<aside class="drawer" part="drawer" aria-hidden="true">
  <div class="drawerHeader" part="drawerHeader">
    <div class="drawerTitle" part="drawerTitle"><slot name="drawer-title">Menu</slot></div>
    <button class="closeBtn" part="closeButton" type="button" aria-label="Close menu"><span class="x" aria-hidden="true"></span></button>
  </div>

  <div class="drawerBody" part="drawerBody">
    <slot name="drawer"></slot>
  </div>
</aside>
`;

export class CtlSiteShell extends HTMLElement {
  static get observedAttributes() {
    return ["open", "sticky"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._menuBtn = this.shadowRoot.querySelector(".menuBtn");
    this._closeBtn = this.shadowRoot.querySelector(".closeBtn");
    this._overlay = this.shadowRoot.querySelector(".overlay");
    this._drawer = this.shadowRoot.querySelector(".drawer");

    this._onKeyDown = this._onKeyDown.bind(this);
    this._onResize = this._onResize.bind(this);
  }

  connectedCallback() {
    this._menuBtn.addEventListener("click", () => this.open = true);
    this._closeBtn.addEventListener("click", () => this.open = false);
    this._overlay.addEventListener("click", () => this.open = false);
    window.addEventListener("keydown", this._onKeyDown);
    window.addEventListener("resize", this._onResize);

    this._syncA11y();
  }

  disconnectedCallback() {
    window.removeEventListener("keydown", this._onKeyDown);
    window.removeEventListener("resize", this._onResize);
  }

  attributeChangedCallback() {
    this._syncA11y();
  }

  get open() { return this.hasAttribute("open"); }
  set open(v) {
    const next = !!v;
    next ? this.setAttribute("open", "") : this.removeAttribute("open");
    this._syncA11y();
    this.dispatchEvent(new CustomEvent("ctl-toggle", {
      bubbles: true,
      composed: true,
      detail: { open: next }
    }));
  }

  _syncA11y() {
    const isOpen = this.open;
    this._drawer.setAttribute("aria-hidden", isOpen ? "false" : "true");
    this._menuBtn.setAttribute("aria-expanded", isOpen ? "true" : "false");
  }

  _onKeyDown(e) {
    if (!this.open) return;
    if (e.key === "Escape") {
      e.preventDefault();
      this.open = false;
    }
  }

  _onResize() {
    // Close drawer when switching to desktop breakpoint to avoid stuck open state
    if (window.matchMedia("(min-width: 861px)").matches && this.open) {
      this.open = false;
    }
  }
}

customElements.define("ctl-site-shell", CtlSiteShell);
